import { Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const isLive = (u) => (u.payments_disabled || u.role === "parent")
  ? !!u.profile_completed && (u.role === "parent" || u.vendor_status === "approved")
  : ["active", "linked"].includes(u.subscription_status) || !!u.bundle_member || !!u.free_access;

const buildSteps = (u) => {
  const parent = u.role === "parent";
  const approved = parent || u.vendor_status === "approved";
  const steps = [{ key: "profile", label: "Profile", done: !!u.profile_completed }];
  if (!parent) {
    const rejected = u.vendor_status === "rejected";
    steps.push({ key: "approval", label: rejected ? "Not approved" : "Approval", done: approved, error: rejected });
  }
  if (!u.payments_disabled && !parent) steps.push({ key: "payment", label: "Payment", done: approved && isLive(u) });
  steps.push({ key: "live", label: "Live", done: false });
  return steps;
};

const HINTS = {
  profile: "Complete your profile below. It only takes a few minutes.",
  approval: "Thanks! Our team is reviewing your profile. We'll email you once it's approved, usually within 1–2 business days.",
  rejected: "Your application wasn't approved. Reply to your registration email and we'll help you sort it out.",
  payment: "You're approved! Choose your plan to go live.",
  payment_parent: "Choose your plan to unlock the community.",
};

const hintFor = (step, role) => {
  if (!step) return null;
  if (step.error) return HINTS.rejected;
  if (step.key === "payment" && role === "parent") return HINTS.payment_parent;
  return HINTS[step.key];
};

export const OnboardingProgress = () => {
  const { user } = useAuth();
  if (!user || user.role === "admin" || isLive(user)) return null;
  const steps = buildSteps(user);
  const current = steps.findIndex((s) => !s.done);
  const hint = hintFor(steps[current], user.role);

  return (
    <nav aria-label="Onboarding progress" className="mb-8 fade-in" data-testid="onboarding-progress">
      <ol className="flex items-center w-full">
        {steps.map((s, i) => {
          const isCurrent = i === current;
          const circle = s.error
            ? "bg-terracotta text-white border-terracotta"
            : s.done
              ? "bg-sage text-white border-sage"
              : isCurrent
                ? "bg-white text-sage-dark border-sage ring-4 ring-sage/20"
                : "bg-white text-forest/40 border-sage/20";
          return (
            <li key={s.key} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`} data-testid={`onboarding-step-${s.key}`} data-state={s.error ? "error" : s.done ? "done" : isCurrent ? "current" : "upcoming"} aria-current={isCurrent ? "step" : undefined}>
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-black transition-colors duration-300 ${circle}`}>
                  {s.error ? <X className="w-4 h-4" /> : s.done ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className={`text-[11px] sm:text-xs font-bold uppercase tracking-wide whitespace-nowrap ${s.error ? "text-terracotta-dark" : isCurrent ? "text-forest" : s.done ? "text-sage-dark" : "text-forest/40"}`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 sm:mx-3 mb-5 rounded-full bg-sage/15 overflow-hidden">
                  <div className={`h-full bg-sage transition-[width] duration-500 ${s.done ? "w-full" : "w-0"}`} />
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {hint && (
        <p className={`mt-4 text-sm text-center rounded-2xl px-4 py-2.5 border ${steps[current].error ? "bg-terracotta-light/50 border-terracotta/30 text-terracotta-dark" : "bg-sage-light/50 border-sage/20 text-forest/80"}`} data-testid="onboarding-next-hint">
          {hint}
        </p>
      )}
    </nav>
  );
};
